import { NodeToolbar, Position, useNodeId } from "@xyflow/react";
import classNames from "classnames";
import { Text, ToggleSwitch } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback, useEffect } from "react";
import {
  ActiveButtonType,
  Actor,
  floatingToolbarActiveButtonAtom,
  NodeType,
  saveCanvasSetter,
  Service,
  sidebarOpenNodeIdAtom,
  toggleZapStepNodesAndEdgesSetterAtomFamily,
  ZapMetadata,
  nodesAtom,
  closeSecondaryToolbarMenuSetter,
  determineAssetType,
  explainNodeSetter,
  isTourOrExplanationRunningAtom,
  emitUserInteractionEventSetter,
  capitalizedAssetTypeDisplayAtom,
  AssetType,
  DisplayLinkAsType,
} from "@zapier/canvas-state";
import { AppIcon } from "../AppIcon";
import { ButtonOrLinkWithTooltip } from "../ButtonOrLinkWithTooltip";
import { FloatingActorTypeahead } from "./FloatingActorTypeahead";
import { FloatingAppTypeahead } from "./FloatingAppTypeahead";
import { FloatingColorPicker } from "./FloatingColorPicker";
import { FloatingLinkController } from "./FloatingLinkController";
import styles from "./FloatingInlineToolbar.module.css";
import { useCopilotChat } from "@zapier/ai-toolkit/client";

type Props = {
  color?: string;
  showAddAppButton?: boolean;
  selectedApps?: Service[];
  actors: Actor[] | undefined;
  hasNotes: boolean;
  hasZap?: boolean;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  zap?: ZapMetadata;
  nodeType: NodeType;
  link?: string;
  displayLinkAs?: DisplayLinkAsType;
  assetIds?: {
    tableId?: string;
    interfaceProjectId?: string;
    interfacePageId?: string;
    chatbotId?: string;
    zapId?: string;
    agentId?: string;
  };
  isAssetUnlinked: boolean;
};

type LinkTooltipProps = {
  assetType: AssetType;
  link: string | undefined;
  assetIds:
    | {
        tableId?: string;
        interfaceProjectId?: string;
        interfacePageId?: string;
        chatbotId?: string;
        zapId?: string;
        agentId?: string;
      }
    | undefined;
  capitalizedAssetType: string;
};

function getLinkButtonTooltip({
  assetType,
  link,
  assetIds,
  capitalizedAssetType,
}: LinkTooltipProps) {
  const hasAssetId = assetIds && Object.values(assetIds).some((value) => value);

  if (hasAssetId && assetType) {
    return `Edit linked ${capitalizedAssetType}`;
  }

  if (assetType) {
    return `Link existing ${capitalizedAssetType}`;
  }

  return `${link ? "Edit" : "Embed"} link `;
}

function getSecondaryToolbarPosition(activeButton: ActiveButtonType) {
  if (activeButton === "link") {
    return Position.Bottom;
  }

  return Position.Top;
}

function getSecondaryToolbarOffset(activeButton: ActiveButtonType) {
  if (activeButton === "link") {
    return 10;
  }

  return 68;
}

const NO_SECONDARY_MENU_BUTTONS = ["notes", "more"];

export function FloatingInlineToolbar({
  color,
  selectedApps,
  actors,
  hasNotes,
  hasZap,
  isZapExpanded,
  isZapNotFound,
  zap,
  nodeType,
  link,
  displayLinkAs,
  assetIds,
  isAssetUnlinked,
}: Props) {
  const id = useNodeId() as string;
  const hasApp = Boolean(selectedApps && selectedApps.length > 0);
  const setSidebarOpenNodeId = useSetAtom(sidebarOpenNodeIdAtom);
  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const actor = actors?.[0] ?? null;

  const isZapNode = nodeType === "zap";
  const zapSteps = zap?.steps || [];
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesSetterAtomFamily(id),
  );
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const assetType = determineAssetType(selectedApps, nodeType);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  const explainNode = useSetAtom(explainNodeSetter);
  const emitUserInteractionEvent = useSetAtom(emitUserInteractionEventSetter);

  const isTourOrExplanationRunning = useAtomValue(
    isTourOrExplanationRunningAtom,
  );
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);
  const capitalizedAssetType = getAssetDisplayName(assetType);

  // Or else the secondary menu won't reset when toolbar unmounts
  useEffect(() => {
    return () => {
      closeSecondaryMenu();
    };
  }, [closeSecondaryMenu]);

  useEffect(() => {
    if (isZapExpanded) {
      closeSecondaryMenu(); // Or else this menu will get in the way of the expanded zap steps
    }
  }, [isZapExpanded, closeSecondaryMenu]);

  const toggleSidebar = useCallback(
    (type: ActiveButtonType) => {
      const isAlreadyOpen = type === activeButton;
      setSidebarOpenNodeId(isAlreadyOpen ? "" : id);
    },
    [activeButton, setSidebarOpenNodeId, id],
  );

  const onButtonClick = useCallback(
    (type: ActiveButtonType) => {
      const isAlreadyOpen = type === activeButton;

      setActiveButton(isAlreadyOpen ? null : type);
    },
    [activeButton, setActiveButton],
  );

  const saveZapNodeToBE = useAtomCallback(
    useCallback(
      async (get) => {
        const nodes = get(nodesAtom);
        const updatedNodes = nodes.map((node) => {
          const isZapNode = node.id === id;

          if (!isZapNode) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: !isZapExpanded,
            },
          };
        });

        void saveToBackend({ nodes: updatedNodes });
      },
      [id, saveToBackend, isZapExpanded],
    ),
  );

  const toggleZapSteps = useCallback(() => {
    setActiveButton(null);
    toggleZapStepsAndEdges(Boolean(isZapExpanded));
    void saveZapNodeToBE();
  }, [isZapExpanded, saveZapNodeToBE, setActiveButton, toggleZapStepsAndEdges]);

  const copilot = useCopilotChat();

  const handleExplainNode = useCallback(async () => {
    if (!copilot) {
      return;
    }

    try {
      await explainNode({
        copilot,
        nodeId: id,
      });

      // Track explain button click with context
      await emitUserInteractionEvent({
        eventAction: "click",
        interactionGoal: "Node explanation requested",
        rawText: "Explain",
        optionSelected: `nodeType: ${nodeType}, assetType: ${assetType}`,
      });
    } catch (error) {
      console.error("Failed to explain node:", error);
    }
  }, [id, explainNode, copilot, emitUserInteractionEvent, nodeType, assetType]);

  const componentMapByType = {
    color: <FloatingColorPicker color={color!} />,
    link: (
      <FloatingLinkController
        zap={zap}
        hasZap={hasZap}
        isZapNotFound={isZapNotFound}
        assetType={assetType}
        link={link}
        displayLinkAs={displayLinkAs}
        assetIds={assetIds}
      />
    ),
    app: (
      <FloatingAppTypeahead
        selectedApps={selectedApps}
        isZapNode={isZapNode}
        hasLink={Boolean(link)}
      />
    ),
    actor: <FloatingActorTypeahead actors={actors} />,
  };

  // Tooltip moves to the bottom so it doesn't block the color picker options
  const tooltipPlacement = activeButton === "color" ? "bottom" : "top";

  return (
    <>
      {activeButton && (
        <NodeToolbar
          className="nopan"
          position={getSecondaryToolbarPosition(activeButton)}
          offset={getSecondaryToolbarOffset(activeButton)}
        >
          {!NO_SECONDARY_MENU_BUTTONS.includes(activeButton) &&
            componentMapByType[activeButton as keyof typeof componentMapByType]}
        </NodeToolbar>
      )}
      <NodeToolbar position={Position.Top} offset={15} className="nopan">
        <div className={styles.floatingToolbarWrapper}>
          <button
            onClick={handleExplainNode}
            className={classNames(styles.buttonNoStyle, styles.buttonWithText, {
              [styles.disabledButton]: isTourOrExplanationRunning,
            })}
            disabled={isTourOrExplanationRunning}
          >
            <Icon name="actionAIText" size={20} />
            <Text type="MinimalPrint3" color="TextInverted">
              Explain
            </Text>
          </button>
          {/* Don't show color editor for unlinked assets - preserves ghost node appearance */}
          {!isAssetUnlinked && (
            <ButtonOrLinkWithTooltip
              content="Edit color"
              onClick={() => onButtonClick("color")}
              placement={tooltipPlacement}
              className={classNames(styles.buttonNoStyle, {
                [styles.activeButton]: activeButton === "color",
              })}
            >
              <div className={styles.colorDotOuterBorder}>
                <div className={styles.colorDotInnerBorder}>
                  <div
                    className={styles.colorDot}
                    style={{ backgroundColor: color }}
                  />
                </div>
              </div>
            </ButtonOrLinkWithTooltip>
          )}
          <button
            className={classNames(styles.buttonNoStyle, styles.buttonWithText, {
              [styles.activeButton]: activeButton === "app",
            })}
            onClick={() => onButtonClick("app")}
          >
            {hasApp ? (
              <AppIcon app={selectedApps![0]} hasBorder={false} />
            ) : isZapNode ? (
              <div className={styles.zapIconWrapper}>
                <Icon name="miscBoltAltFill" color="BrandOrange" size={16} />
              </div>
            ) : (
              <Icon name="navApps" size={20} />
            )}
            <Text
              type="MinimalPrint3"
              color="TextInverted"
            >{`${hasApp || isZapNode ? "Change" : "Add"} app`}</Text>
          </button>
          <ButtonOrLinkWithTooltip
            content={getLinkButtonTooltip({
              assetType,
              link,
              assetIds,
              capitalizedAssetType,
            })}
            onClick={() => onButtonClick("link")}
            placement={tooltipPlacement}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "link",
            })}
          >
            <Icon name="actionLink" size={20} />
          </ButtonOrLinkWithTooltip>
          <ButtonOrLinkWithTooltip
            content={`${hasNotes ? "Edit" : "Add"} note`}
            onClick={() => {
              onButtonClick("note");
              toggleSidebar("note");
            }}
            placement={tooltipPlacement}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "note",
            })}
          >
            <Icon name="fileFilled" size={20} />
          </ButtonOrLinkWithTooltip>
          <ButtonOrLinkWithTooltip
            content={`${actor ? "Edit" : "Add"} people involved`}
            onClick={() => onButtonClick("actor")}
            placement={tooltipPlacement}
            className={classNames(styles.buttonNoStyle, {
              [styles.activeButton]: activeButton === "actor",
            })}
          >
            <Icon name="personCircle" size={20} />{" "}
          </ButtonOrLinkWithTooltip>
          {zapSteps && zapSteps.length > 0 && (
            <div className={classNames(styles.flexContainer)}>
              <ToggleSwitch checked={isZapExpanded} onChange={toggleZapSteps} />
              <Text type="Body1" color="TextInverted">
                Show steps
              </Text>
            </div>
          )}
          <ButtonOrLinkWithTooltip
            content="More options"
            onClick={() => {
              onButtonClick("more");
              toggleSidebar("more");
            }}
            placement={tooltipPlacement}
            className={classNames(styles.buttonNoStyle, styles.noDivider, {
              [styles.activeButton]: activeButton === "more",
            })}
          >
            <Icon name="navMoreHoriz" size={20} />
          </ButtonOrLinkWithTooltip>
        </div>
      </NodeToolbar>
    </>
  );
}
